import gsap from 'gsap'
import { module } from 'modujs';
import { lerp } from '../utils/maths'
import { shuffle } from '../utils/array';
import { lazyLoadImage, animDepixelate } from '../utils/image';

function getVec2Dist(vec1, vec2) {
    return Math.sqrt(Math.pow(vec1.x - vec2.x, 2) + Math.pow(vec1.y - vec2.y, 2))
}

export default class extends module {
    constructor(m) {
        super(m);

        this.rotationAmplitude = 16;
        this.distanceToSpawn = 80;
        this.delayToSpawn = 500; // in ms

        this.minImages = 60;
        this.$images = [];
        this.timelines = [];
        this.currentIndex = 0;

        this.lastSpawnTime = Date.now();
    }

    init() {
        if(!this.$('image').length) {
            console.error('[FancyGallery] no images given')
            return false;
        }

        this.compute()
        this.populateImages()
        this.bindEvents()
        this.render()
    }

    compute() {
        this.isMobile = window.innerWidth < 700
        this.canHover = window.matchMedia("(hover: hover)").matches

        const BCR = this.el.getBoundingClientRect();
        const center = {
            x: BCR.width/2,
            y: BCR.height/2
        }

        this.target = { ...center };
        this.lerp = { ...center };
        this.lastSpawn = { ...center };
    }

    checkResize() {
        if(!this.resizeTick) {
            this.resizeTick = true
            requestAnimationFrame(() => {
                this.compute()
                this.resizeTick = false
            })
        }
    }

    bindEvents() {

        this.onMouseEnter = (e) => {
            const enterPos = {
                x: e.layerX,
                y: e.layerY
            }
            this.target = { ...enterPos };
            this.lerp = { ...enterPos };
            this.lastSpawn = { ...enterPos };
        }

        this.onMouseMove = (e) => {
            this.target = {
                x: e.layerX,
                y: e.layerY
            }
        }

        if(this.canHover && !this.isMobile) {
            this.el.addEventListener('mouseenter', this.onMouseEnter)
            this.el.addEventListener('mousemove', this.onMouseMove);
        }

        this.checkResizeBind = this.checkResize.bind(this)
        window.addEventListener('resize', this.checkResizeBind)
    }

    unbindEvents() {
        this.el.removeEventListener('mouseenter', this.onMouseEnter)
        this.el.removeEventListener('mousemove', this.onMouseMove);

        window.removeEventListener('resize', this.checkResizeBind)
    }

    render() {
        this.lerp = {
            x: lerp(this.lerp.x, this.target.x, 0.1),
            y: lerp(this.lerp.y, this.target.y, 0.1)
        }

        this.checkSpawn()

        this.raf = requestAnimationFrame(this.render.bind(this));
    }

    checkSpawn() {
        if(this.canHover && !this.isMobile) {
            const dist = Math.abs(getVec2Dist(this.lastSpawn, this.lerp))

            if(dist > this.distanceToSpawn) {
                this.spawn()
            }
        } else {
            const delay = Date.now() - this.lastSpawnTime

            if(delay > this.delayToSpawn) {
                this.spawn()
            }
        }
    }

    spawn() {
        this.lastSpawn = this.lerp;
        this.lastSpawnTime = Date.now();

        const target = this.$images[this.currentIndex % this.$images.length]
        const inner = target.querySelector('.c-image_inner')

        const img = target.querySelector('img')
        if(img) {
            if(!img.classList.contains('-loaded')) {
                lazyLoadImage(img, null)
            }
        }

        const tl = gsap.timeline({
            onComplete: () => {
                this.timelines.splice(this.timelines.indexOf(tl), 1);
            }
        })
        tl.set(target, { force3D: true, x: this.lastSpawn.x, y: this.lastSpawn.y, zIndex: this.currentIndex })
        tl.set(inner, { opacity: 0, scale: 0.5, force3D: true })

        tl.to(inner, { opacity: 1, duration: .15 }, 0)
        tl.to(inner, { force3D: true, scale: 1, rotation: Math.random()*this.rotationAmplitude-this.rotationAmplitude/2, duration: .25 }, 0)

        // tl.addLabel('appeared')
        tl.to(inner, { force3D: true, scale: 1.2, duration: 1 })
        tl.to(inner, { force3D: true, scale: 0.5, rotation: Math.random()*this.rotationAmplitude-this.rotationAmplitude/2, opacity: 0, duration: .3, ease: 'power2.in' })
        this.timelines.push(tl);

        this.currentIndex++;
    }

    populateImages() {
        const images = shuffle(Array.from(this.$('image')));
        // shuffle
        for(let image of images) {
            image.parentNode.appendChild(image)
        }

        // Duplicate as much as needed to be above minImages
        for(let i = 0; i < Math.ceil(this.minImages/images.length); i++) {
            for(let image of Array.from(images)) {
                const clone = image.cloneNode(true);
                image.parentNode.appendChild(clone);
            }
        }

        this.$images = Array.from(this.$('image'))
    }

    destroy() {
        super.destroy();

        for(let tl of this.timelines) {
            tl.kill();
            tl = null;
        }

        this.unbindEvents();
    }
}
